/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { c as _c } from "react/compiler-runtime";
import ImageComponent from "@/components/ui/image";
import dynamic from "next/dynamic";
const MovingTag = dynamic(() => import("./MovingTag"), {
  ssr: false,
  loading: () => null
});
import Tag from "./components/Tag";
import { memo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { sanitizeText } from "@/utils/seo";

// ─── Banner Carousel ──────────────────────────────────────────────────────────
const BannerCarousel = memo(t0 => {
  const $ = _c(21);
  const {
    images,
    businessName
  } = t0;
  const [currentIndex, setCurrentIndex] = useState(0);
  let t1;
  let t2;
  if ($[0] !== images) {
    t1 = () => {
      if (!images || images.length <= 1) {
        return;
      }
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % images.length);
      }, 4000);
      return () => clearInterval(interval);
    };
    t2 = [images];
    $[0] = images;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!images || images.length === 0) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="absolute inset-0 bg-gradient-to-br from-[#037A6A] to-[#0a2421]" />;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  let t3;
  if ($[4] !== businessName) {
    t3 = businessName ? sanitizeText(businessName) : "";
    $[4] = businessName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const cleanBusinessName = t3;
  let t4;
  if ($[6] !== cleanBusinessName || $[7] !== currentIndex || $[8] !== images) {
    let t5;
    if ($[10] !== cleanBusinessName || $[11] !== currentIndex) {
      t5 = (img, index) => <motion.div key={img} initial={{
        opacity: 0
      }} animate={{
        opacity: index === currentIndex ? 1 : 0
      }} transition={{
        duration: 1.5,
        ease: "easeInOut"
      }} className="absolute inset-0"><ImageComponent src={img} alt={cleanBusinessName ? `${cleanBusinessName} cover photo ${index + 1}` : `cover-${index}`} width={2048} height={2048} className="object-cover w-full h-full scale-105" priority={true} unoptimized={true} quality={90} sizes="100vw" /></motion.div>;
      $[10] = cleanBusinessName;
      $[11] = currentIndex;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = images.map(t5);
    $[6] = cleanBusinessName;
    $[7] = currentIndex;
    $[8] = images;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <div className="absolute inset-0 overflow-hidden">{t4}</div>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== currentIndex || $[16] !== images) {
    t6 = images.length > 1 && <div className="absolute bottom-14 left-1/2 -translate-x-1/2 z-10 flex gap-2">{images.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentIndex ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />)}</div>;
    $[15] = currentIndex;
    $[16] = images;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
});
BannerCarousel.displayName = "BannerCarousel";

// ─── Dietary Tags ─────────────────────────────────────────────────────────────
const DietaryTags = memo(t0 => {
  const $ = _c(11);
  const {
    tags
  } = t0;
  let t1;
  if ($[0] !== tags) {
    t1 = tags?.map(_temp);
    $[0] = tags;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="space-x-3 lg:flex items-center justify-center hidden">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== tags) {
    t3 = tags?.map(_temp2);
    $[4] = tags;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="space-x-3 flex items-center justify-center lg:hidden w-full">{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
});
DietaryTags.displayName = "DietaryTags";

// ─── Desktop Hero ─────────────────────────────────────────────────────────────
const DesktopHero = memo(t0 => {
  const $ = _c(33);
  const {
    vendorData
  } = t0;
  let t1;
  if ($[0] !== vendorData?.vendorImages) {
    t1 = vendorData?.vendorImages || [];
    $[0] = vendorData?.vendorImages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const images = t1;
  const image1 = images.length > 0 ? images[0] : null;
  const image2 = images.length > 1 ? images[1] : null;
  let t2;
  if ($[2] !== vendorData) {
    t2 = vendorData?.area_name ? `• ${vendorData.area_name.toUpperCase()}` : "";
    $[2] = vendorData;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="text-[10px] font-bold tracking-[0.2em] text-[#8a8175] uppercase mb-4 flex items-center gap-1 font-urbanist">ARTISAN COLLECTIVE{" "}{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = vendorData?.business_name || "";
  let t5;
  if ($[6] !== t4) {
    t5 = <h1 className="font-serif text-5xl text-[#2F3A35] font-bold mb-6 leading-tight">{t4}</h1>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== vendorData) {
    t6 = vendorData?.categories_detail?.length > 0 && <div className="flex gap-2 mb-6">{vendorData.categories_detail.map(_temp3)}</div>;
    $[8] = vendorData;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = vendorData?.story || vendorData?.description || "";
  let t8;
  if ($[10] !== t7) {
    t8 = <p className="text-[#666666] text-sm leading-relaxed max-w-md mb-8 font-urbanist">{t7}</p>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t3 || $[13] !== t5 || $[14] !== t6 || $[15] !== t8) {
    t9 = <div className="w-1/2 flex flex-col items-start pr-8">{t3}{t5}{t6}{t8}</div>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== image1) {
    t10 = image1 && <div className="w-[300px] aspect-square rounded-xl overflow-hidden shadow-sm relative -mt-12 bg-white"><ImageComponent src={image1} alt="Vendor Image 1" width={300} height={300} className="object-cover w-full h-full" sizes="300px" /></div>;
    $[17] = image1;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== image1 || $[20] !== image2) {
    t11 = image2 ? <div className="w-[300px] aspect-square rounded-xl overflow-hidden shadow-sm relative mt-12 bg-white"><ImageComponent src={image2} alt="Vendor Image 2" width={300} height={300} className="object-cover w-full h-full" sizes="300px" /></div> : image1 ? <div className="w-[300px] aspect-square rounded-xl overflow-hidden shadow-sm relative mt-12 bg-white"><ImageComponent src={image1} alt="Vendor Image 2 Duplicate" width={300} height={300} className="object-cover w-full h-full" sizes="300px" /></div> : <div className="w-full flex justify-end gap-6"><div className="w-[300px] aspect-square rounded-xl bg-gray-200/50 shadow-sm relative -mt-12" /><div className="w-[300px] aspect-square rounded-xl bg-gray-200/50 shadow-sm relative mt-12" /></div>;
    $[19] = image1;
    $[20] = image2;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className="w-1/2 flex items-center justify-end gap-6 relative">{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t9) {
    t13 = <div className="max-w-7xl mx-auto flex items-center justify-between gap-8">{t9}{t12}</div>;
    $[25] = t12;
    $[26] = t9;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== vendorData) {
    t14 = (vendorData?.metadata?.tags?.length > 0 || vendorData?.metadata?.dietary_categories?.length > 0) && <div className="w-full mt-12 pt-6">{vendorData?.metadata?.tags?.length > 0 && <div className="w-full overflow-hidden mb-4"><MovingTag tags={vendorData.metadata.tags} /></div>}{vendorData?.metadata?.dietary_categories?.length > 0 && <div className="max-w-7xl mx-auto flex justify-center w-full"><DietaryTags tags={vendorData.metadata.dietary_categories} /></div>}</div>;
    $[28] = vendorData;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = <div className="hidden md:block w-full bg-[#f4f3ef] px-12 py-16">{t13}{t14}</div>;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
});
DesktopHero.displayName = "DesktopHero";

// ─── Mobile Hero ──────────────────────────────────────────────────────────────
const MobileHero = memo(t0 => {
  const $ = _c(33);
  const {
    vendorData
  } = t0;
  let t1;
  if ($[0] !== vendorData?.vendorImages) {
    t1 = vendorData?.vendorImages || [];
    $[0] = vendorData?.vendorImages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const vendorImages = t1;
  const t2 = vendorData?.business_name;
  let t3;
  if ($[2] !== t2 || $[3] !== vendorImages) {
    t3 = <BannerCarousel images={vendorImages} businessName={t2} />;
    $[2] = t2;
    $[3] = vendorImages;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/50" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <div className="relative w-full aspect-square overflow-hidden">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== vendorData?.business_name || $[9] !== vendorData?.media_url) {
    t6 = vendorData?.media_url && <div className="absolute -top-8 w-14 h-14 min-w-[56px] max-w-[56px] min-h-[56px] max-h-[56px] rounded-lg border-2 border-white shadow-md overflow-hidden bg-white flex-none"><ImageComponent alt={`${vendorData?.business_name || "Vendor"} logo`} src={vendorData?.media_url} height={56} width={56} objectFit="contain" className="w-full h-full object-contain" priority={true} addPlaceholder={false} sizes="56px" /></div>;
    $[8] = vendorData?.business_name;
    $[9] = vendorData?.media_url;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = vendorData?.business_name || "";
  let t8;
  if ($[11] !== t7) {
    t8 = <div className="font-montserrat font-semibold text-sm sm:text-base text-[#4A4A4A] tracking-tight leading-snug mb-2 text-center px-4 uppercase">{t7}</div>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== vendorData) {
    t9 = vendorData?.area_name && <div className="flex items-center gap-1 text-gray-500 mb-5"><MapPin size={16} className="text-[#037A6A]" /><span className="inline-flex items-center py-1 rounded-full text-xs font-semibold font-outfit tracking-wide transition-all">{vendorData.area_name}</span></div>;
    $[13] = vendorData;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== vendorData?.categories_detail) {
    t10 = vendorData?.categories_detail?.map(_temp4);
    $[15] = vendorData?.categories_detail;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = <div className="flex flex-wrap justify-center gap-2 ">{t10}</div>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = vendorData?.metadata?.dietary_categories;
  let t13;
  if ($[19] !== t12) {
    t13 = <div className="w-full"><DietaryTags tags={t12} /></div>;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t11 || $[22] !== t13 || $[23] !== t6 || $[24] !== t8 || $[25] !== t9) {
    t14 = <div className="px-8 relative z-10 w-full -mt-10 mb-6"><div className="bg-[#f8f9fa] rounded-xl shadow-sm border border-gray-200 p-0 flex flex-col items-center relative pt-5 pb-5 w-full">{t6}{t8}{t9}{t11}{t13}</div></div>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== vendorData?.metadata?.tags) {
    t15 = vendorData?.metadata?.tags?.length > 0 && <div className="w-full bg-white py-3 shadow-sm"><MovingTag tags={vendorData?.metadata?.tags} /></div>;
    $[27] = vendorData?.metadata?.tags;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14 || $[30] !== t15 || $[31] !== t5) {
    t16 = <div className="md:hidden w-full bg-white">{t5}{t14}{t15}</div>;
    $[29] = t14;
    $[30] = t15;
    $[31] = t5;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
});
MobileHero.displayName = "MobileHero";

// ─── Main VendorsHero ─────────────────────────────────────────────────────────
const VendorsHero = memo(t0 => {
  const $ = _c(2);
  const {
    vendorData
  } = t0;
  let t1;
  if ($[0] !== vendorData) {
    t1 = <><DesktopHero vendorData={vendorData} /><MobileHero vendorData={vendorData} /></>;
    $[0] = vendorData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
});
VendorsHero.displayName = "VendorsHero";
export default VendorsHero;
function _temp(tag) {
  return <Tag key={tag} title={tag} />;
}
function _temp2(tag_0) {
  return <div key={tag_0} className="font-outfit font-normal text-[#1E1E1E] border-l border-[#D9D9D9] px-[10px] first:border-l-0">{tag_0}</div>;
}
function _temp3(item) {
  return <span key={item.name} className="px-3 py-1 bg-gray-200/70 text-gray-500 text-[10px] font-bold rounded-full uppercase tracking-wider font-urbanist">{item.name}</span>;
}
function _temp4(item) {
  return <span key={item.name} className="bg-v2-surface-container-high text-v2-on-surface-variant font-outfit text-[10px] px-3 py-1 rounded-full uppercase tracking-wider inline-flex items-center">{item.name}</span>;
}